/**
 * CockpitPage — Owner-Index-Landing (M-UX-4.2, `plan-ux-4.2-cockpit.md` §1/§9).
 *
 * Ersetzt (hinter `UX_COCKPIT_ENABLED`) das bisherige leere Rechnungsformular
 * als `/dashboard`-Index: ein Handlungs-Dashboard aus isolierten Kacheln
 * (offene Posten, Umsatz/Ausgaben, Posteingang, Bank-Abgleich, Belege,
 * Steuer-Countdown, Schnellaktionen, Aktivierungs-Checkliste). Alle Daten
 * kommen aus dem bereits gemergten 4.1-Loader (`loaders/cockpitLoaders.ts`)
 * plus bestehenden APIs — diese Datei baut KEINE neuen Datenlader außer dem
 * puren Steuer-Countdown-Helper (`utils/ustvaDeadline.ts`).
 *
 * Kachel-Isolation (§1, aus 4.1-Loader-JSDoc): jede Kachel lädt über einen
 * eigenen `useCockpitTile`-Hook-Aufruf — ein Fehler in einer Kachel zeigt
 * dort „—" + Retry, crasht aber nie die Seite oder andere Kacheln
 * (`ErrorBoundary` auf Routen-Ebene bleibt der äußere Sicherheitsnetz, wird
 * hier aber bewusst nicht ausgelöst).
 *
 * @module pages/CockpitPage
 */

import React, { lazy, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { doc as fsDoc, getDoc } from 'firebase/firestore';
import {
  IconOpenItems,
  IconTrend,
  IconExpense,
  IconInbox,
  IconBank,
  IconScan,
  IconVat,
  IconSuccess,
  IconCircle,
  IconChevronRight,
} from '../icons';
import { useAuth } from '../context/AuthContext';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import { useIsAdvisorContext } from '../hooks/useIsAdvisorContext';
import KanzleiProfileCard from '../components/advisor/KanzleiProfileCard';
import { db } from '../firebaseConfig';
import logger from '../utils/logger';
import { formatCurrency } from '../utils/formatters';
import { hasSellerRequiredFields } from '../utils/sellerProfile';
import { centsToEuros } from '../utils/moneyUtils';
import { computeNextUstvaDeadline } from '../utils/ustvaDeadline';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { Spinner } from '../components/common/Spinner';
import { ChartCard, type ChartLoadState } from '../components/cockpit/ChartCard';
import GrowthBadge from '../components/cockpit/GrowthBadge';
import ResultYtdTile from '../components/cockpit/ResultYtdTile';
import VatReserveTile from '../components/cockpit/VatReserveTile';
import DsoTile from '../components/cockpit/DsoTile';
import DsoTrendTile from '../components/cockpit/DsoTrendTile';
import OverdueFocusList from '../components/cockpit/OverdueFocusList';
import NettoCashTile from '../components/cockpit/NettoCashTile';
import CockpitHeroTile from '../components/cockpit/CockpitHeroTile';
import { computeDso } from '../utils/cockpitReceivables';
import { computeNettoCash, sumNetForWeeks } from '../utils/cockpitNettoCash';
import { bucketMonthly, ageOpenItems, type AgingBucketId, type MonthlyBucket, type AgingBucket } from '../utils/cockpitChartData';
import { computeRevenueGrowth } from '../utils/cockpitYtd';
import { buildVatReturn, toVatInputAggregate, type VatIncomeEntry } from '../utils/vatReturn';
import { deriveVatReserveView } from '../utils/vatReserve';
import { currentQuarterPeriod } from '../utils/expenseSummary';
import {
  loadOpenItemRows,
  summarizeOpenItems,
  loadIncomeForUser,
  loadVatIncomeForUser,
  sumMonth,
  loadInboundNewCount,
  loadBankUnmatchedCount,
  deriveChecklist,
  sumBookedExpensesMonth,
  type OpenItemsSummary,
  type CockpitBoundedCount,
  type ChecklistItem,
  type CockpitOpenItemRow,
} from '../loaders/cockpitLoaders';
import { getExpensesForUser } from '../api/expensesApi';
import { getScannedReceiptsForUser } from '../api/scannedReceiptsApi';
import { listMyAdvisorInvites } from '../api/advisorInviteApi';
import { canUseInbound } from '../config/inboundAccess';
import { UNIFIED_INBOX_ENABLED } from '../config/unifiedInboxAccess';
import { canUseBankReconcile } from '../config/bankReconcileAccess';
import { canUseReceiptScan } from '../config/receiptScanAccess';
import { resolveTier, type Tier } from '../config/entitlements';
import { canInviteAdvisor } from '../config/advisorInviteAccess';
import { INVOICE_FORM_ROUTE } from '../config/cockpitAccess';
import { NAV_ACTIONS, type NavContext } from '../config/navigation';
import type { IncomeEntry } from '../utils/expenseSummary';
import type { Expense } from '../types/expenseTypes';

// M-F (§11 Spike, architecture-ux-density-v2.md): recharts lazy-geladen —
// eigener Bundle-Chunk, nur auf der Cockpit-Route nachgeladen.
const RevenueExpenseChart = lazy(() => import('../components/cockpit/RevenueExpenseChart'));
const OpenItemsAgingChart = lazy(() => import('../components/cockpit/OpenItemsAgingChart'));

// Feature flags — gespiegelt aus `routes/dashboardRoutePaths.ts`/`config/navigation.ts`
// (bewusste Duplikation, Muster wie dort begründet: kein zentraler Export vorhanden).
const EXPENSES_ENABLED = process.env.REACT_APP_EXPENSES_ENABLED === 'true';
const USTVA_ENABLED = process.env.REACT_APP_USTVA_ENABLED === 'true';

/** Today's date as ISO YYYY-MM-DD (local timezone) — bewusst dupliziert (kein Cross-Import), Muster `cockpitLoaders.ts`. */
function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * `prefers-reduced-motion` (M-F, §10.5 Punkt 6) — reine Media-Query-Abfrage
 * zur Render-Zeit (kein Listener/State nötig: das Cockpit ist keine
 * Langzeit-Session-View, ein Wechsel der OS-Einstellung mitten in der
 * Sitzung ist ein vernachlässigbarer Edge-Case). Defensiv gegen jsdom/ältere
 * Browser ohne `matchMedia` (Test-Umgebung, §19).
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Kurzer Monatsname aus `'YYYY-MM'` in der aktiven Locale (UTC, kein DST-Drift) — für Chart-Tabellen-Fallback/ARIA-Zusammenfassung. */
function formatMonthShort(yearMonth: string, locale: string): string {
  const [y, m] = yearMonth.split('-').map(Number);
  const d = new Date(Date.UTC(y, (m || 1) - 1, 1));
  return new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(d);
}

/** Monatsname ohne Jahr aus `'YYYY-MM'` (z. B. „Jun") — für den Umsatz-Wachstums-Badge (E5). */
function formatMonthNameShort(yearMonth: string, locale: string): string {
  const [y, m] = yearMonth.split('-').map(Number);
  const d = new Date(Date.UTC(y, (m || 1) - 1, 1));
  return new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(d);
}

/** Vorzeichenbehaftetes Ganzzahl-Prozent, z. B. „+18 %"/„-4 %" (locale-neutral, E5). */
function formatSignedPercent(pct: number): string {
  const sign = pct > 0 ? '+' : pct < 0 ? '-' : '';
  return `${sign}${Math.abs(pct)} %`;
}

// ─── Kachel-Isolation: generischer Lade-Hook ───────────────────────────────

type TileState<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error' };

/**
 * Isolierter Lade-Hook für EINE Kachel (§1 Kachel-Isolations-Grundsatz).
 * `enabled=false` ⇒ kein Fetch (Gate ist false, Kachel wird ohnehin nicht
 * gerendert). Ein Fehler wird geloggt und führt zu `status:'error'` — nie zu
 * einem geworfenen Render-Fehler (kein Page-Crash).
 *
 * @param enabled - Gate-Ergebnis der aufrufenden Kachel.
 * @param scopeUid - Lade-Parameter (i. d. R. `effectiveScopeUid`); Änderungen
 *   lösen einen Re-Fetch aus.
 * @param loader - lädt die Kachel-Daten für `scopeUid`.
 */
function useCockpitTile<T>(
  enabled: boolean,
  scopeUid: string,
  loader: (scopeUid: string) => Promise<T>,
): { state: TileState<T>; retry: () => void } {
  const [retryToken, setRetryToken] = useState(0);
  const [state, setState] = useState<TileState<T>>({ status: 'loading' });
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setState({ status: 'loading' });
    loaderRef
      .current(scopeUid)
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', data });
      })
      .catch((err) => {
        if (!cancelled) setState({ status: 'error' });
        logger.error('CockpitPage: Kachel-Ladefehler', err);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, scopeUid, retryToken]);

  return { state, retry: () => setRetryToken((n) => n + 1) };
}

// ─── Kleine Anzeige-Helfer ──────────────────────────────────────────────────

/** Rendert Lade-/Fehler-/Bereit-Zustand generisch; `renderReady` bekommt die geladenen Daten. */
function TileBody<T>({
  state,
  retry,
  renderReady,
}: {
  state: TileState<T>;
  retry: () => void;
  renderReady: (data: T) => React.ReactNode;
}) {
  const { t } = useTranslation('cockpit');

  if (state.status === 'loading') {
    return (
      <div className="flex justify-center py-2">
        <Spinner size="sm" />
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="flex items-center justify-between gap-2">
        <span className="text-secondary">{t('empty.dash')}</span>
        <Button size="sm" variant="secondary" onClick={retry}>
          {t('retry')}
        </Button>
      </div>
    );
  }
  return <>{renderReady(state.data)}</>;
}

/** Formats a bounded count for display — `capped` shows "N+" (§1 Kachel #4/#5). */
function formatBoundedCount(result: CockpitBoundedCount): string {
  return result.capped ? `${result.count}+` : `${result.count}`;
}

// ─── Kompakte Zähl-Kachel (D1, Spec §3.3) ───────────────────────────────────

/**
 * Kompakte Aktivitäts-/Zählkachel (D1, `architecture-ux-density-v3.md` §3.3):
 * EINE ~64px hohe horizontale Zeile `[Icon] Titel + Zahl + Sublabel [›]`
 * statt einer großen, fast leeren `Card` mit nur einer nackten Zahl. Zero-State
 * (`count === 0`) zeigt ein Häkchen + „Alles erledigt" statt einer „0"
 * (Marktführer-Polish). Gemeinsame Schale für {@link InboxTile}/{@link BankTile}/
 * {@link ReceiptsTile}; die Zahl bleibt ein eigener Textknoten (`formatCount`),
 * damit die Bestandstests `getByText('25+')`/`getByText('7')` grün bleiben.
 *
 * @param icon - Kachel-Icon (wird `aria-hidden` gewrappt).
 * @param titleKey - i18n-Key des (entrümpelten) Titels.
 * @param sublabelKey - i18n-Key des statischen Sublabels (Klammer-Kontext, kein Count-Interpolation).
 * @param onNavigate - Klick-/Enter-Navigationsziel der Kachel.
 * @param state - Kachel-Ladezustand aus {@link useCockpitTile}.
 * @param retry - Retry-Callback für den Fehlerzustand.
 * @param getCount - liefert den numerischen Zählwert (Zero-State-Entscheid).
 * @param formatCount - liefert die anzeigefertige Zahl (z. B. „25+").
 */
function CompactCountTile<T>({
  icon,
  titleKey,
  sublabelKey,
  onNavigate,
  state,
  retry,
  getCount,
  formatCount,
}: {
  icon: React.ReactNode;
  titleKey: string;
  sublabelKey: string;
  onNavigate: () => void;
  state: TileState<T>;
  retry: () => void;
  getCount: (data: T) => number;
  formatCount: (data: T) => string;
}) {
  const { t } = useTranslation('cockpit');

  return (
    <Card
      padded={false}
      role="button"
      tabIndex={0}
      onClick={onNavigate}
      onKeyDown={(e) => e.key === 'Enter' && onNavigate()}
      className="p-3 cursor-pointer h-full"
    >
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="text-secondary text-lg shrink-0 flex">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-xs font-semibold text-secondary truncate">{t(titleKey)}</h3>
          <TileBody
            state={state}
            retry={retry}
            renderReady={(data) =>
              getCount(data) === 0 ? (
                <p className="flex items-center gap-1.5 text-sm text-secondary">
                  <IconSuccess aria-hidden={true} className="text-[var(--tone-success-fg)] shrink-0" />
                  {t('countTile.allDone')}
                </p>
              ) : (
                <p className="flex items-baseline gap-1.5">
                  <span className="text-lg font-bold text-primary">{formatCount(data)}</span>
                  <span className="text-xs text-secondary truncate">{t(sublabelKey)}</span>
                </p>
              )
            }
          />
        </div>
        <IconChevronRight aria-hidden={true} className="text-muted shrink-0 text-xs" />
      </div>
    </Card>
  );
}

// ─── Kachel #1 — Offene Posten ──────────────────────────────────────────────

const OpenItemsTile: React.FC<{
  scopeUid: string;
  /** M-F: bubbelt die bereits geladenen `CockpitOpenItemRow[]` (inkl. `dueDateISO`) hoch (§6). */
  onRowsReady?: (rows: CockpitOpenItemRow[]) => void;
  /** M-F: bubbelt einen Ladefehler hoch. */
  onRowsError?: () => void;
}> = ({ scopeUid, onRowsReady, onRowsError }) => {
  const { t } = useTranslation('cockpit');
  const navigate = useNavigate();
  const { state, retry } = useCockpitTile<{ rows: CockpitOpenItemRow[]; summary: OpenItemsSummary }>(
    true,
    scopeUid,
    async (uid) => {
      const rows = await loadOpenItemRows(uid);
      return { rows, summary: summarizeOpenItems(rows) };
    },
  );

  useEffect(() => {
    if (state.status === 'ready') {
      if (onRowsReady) onRowsReady(state.data.rows);
    } else if (state.status === 'error') {
      if (onRowsError) onRowsError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => navigate('/dashboard/offene-posten')}
      onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard/offene-posten')}
      className="cursor-pointer"
    >
      <div className="flex items-center gap-2 mb-2 text-secondary">
        <IconOpenItems aria-hidden={true} />
        <h3 className="text-sm font-semibold">{t('openItems.title')}</h3>
      </div>
      <TileBody
        state={state}
        retry={retry}
        renderReady={(data) => (
          <div>
            <p className="text-xl font-bold text-primary">
              {formatCurrency(centsToEuros(data.summary.openCents))}
            </p>
            {data.summary.overdueCount > 0 && (
              <p className="text-sm mt-1">
                <Badge tone="warning">
                  {data.summary.overdueCount} {t('openItems.overdue')} ·{' '}
                  {formatCurrency(centsToEuros(data.summary.overdueCents))}
                </Badge>
              </p>
            )}
          </div>
        )}
      />
    </Card>
  );
};

// ─── Kachel #2 — Umsatz lfd. Monat ──────────────────────────────────────────

const RevenueTile: React.FC<{
  scopeUid: string;
  onLoaded?: (invoiceCount: number) => void;
  /** §B: feuert, sobald die Kachel `ready` ODER `error` erreicht (Checklisten-
   *  Sichtbarkeits-Entscheid darf erst danach fallen — kein Flacker-Frame). */
  onSettled?: () => void;
  /**
   * M-F (§6 „0 zusätzliche Firestore-Reads"): bubbelt die bereits geladenen
   * `IncomeEntry[]` hoch, damit die Umsatz/Ausgaben-Chart-Kachel dieselben
   * Rohdaten wiederverwenden kann, statt sie ein zweites Mal zu laden.
   */
  onIncomeReady?: (entries: IncomeEntry[]) => void;
  /** M-F: bubbelt einen Ladefehler hoch (Chart-Kachel zeigt dann ebenfalls Fehler). */
  onIncomeError?: () => void;
}> = ({ scopeUid, onLoaded, onSettled, onIncomeReady, onIncomeError }) => {
  const { t } = useTranslation('cockpit');
  const navigate = useNavigate();
  const { state, retry } = useCockpitTile<{ sumCents: number; invoiceCount: number; entries: IncomeEntry[] }>(
    true,
    scopeUid,
    async (uid) => {
      const entries = await loadIncomeForUser(uid);
      return { sumCents: sumMonth(entries, todayISO()), invoiceCount: entries.length, entries };
    },
  );

  useEffect(() => {
    if (state.status === 'ready') {
      if (onLoaded) onLoaded(state.data.invoiceCount);
      if (onSettled) onSettled();
      if (onIncomeReady) onIncomeReady(state.data.entries);
    } else if (state.status === 'error') {
      if (onSettled) onSettled();
      if (onIncomeError) onIncomeError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <Card role="button" tabIndex={0} onClick={() => navigate('/dashboard/archiv')} onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard/archiv')} className="cursor-pointer">
      <div className="flex items-center gap-2 mb-2 text-secondary">
        <IconTrend aria-hidden={true} />
        <h3 className="text-sm font-semibold">{t('revenue.title')}</h3>
      </div>
      <TileBody
        state={state}
        retry={retry}
        renderReady={(data) => (
          <p className="text-xl font-bold text-primary">{formatCurrency(centsToEuros(data.sumCents))}</p>
        )}
      />
    </Card>
  );
};

// ─── Kachel #3 — Ausgaben lfd. Monat ─────────────────────────────────────────

const ExpensesTile: React.FC<{
  scopeUid: string;
  /** M-F: bubbelt die bereits geladenen `Expense[]` hoch (Chart-Wiederverwendung, §6). */
  onExpensesReady?: (expenses: Expense[]) => void;
  /** M-F: bubbelt einen Ladefehler hoch. */
  onExpensesError?: () => void;
}> = ({ scopeUid, onExpensesReady, onExpensesError }) => {
  const { t } = useTranslation('cockpit');
  const navigate = useNavigate();
  const { state, retry } = useCockpitTile<{ cents: number; expenses: Expense[] }>(
    EXPENSES_ENABLED,
    scopeUid,
    async (uid) => {
      const expenses = await getExpensesForUser(uid);
      return { cents: sumBookedExpensesMonth(expenses, todayISO()), expenses };
    },
  );

  useEffect(() => {
    if (state.status === 'ready') {
      if (onExpensesReady) onExpensesReady(state.data.expenses);
    } else if (state.status === 'error') {
      if (onExpensesError) onExpensesError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  if (!EXPENSES_ENABLED) return null;

  return (
    <Card role="button" tabIndex={0} onClick={() => navigate('/dashboard/ausgaben')} onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard/ausgaben')} className="cursor-pointer">
      <div className="flex items-center gap-2 mb-2 text-secondary">
        <IconExpense aria-hidden={true} />
        <h3 className="text-sm font-semibold">{t('expenses.title')}</h3>
      </div>
      <TileBody state={state} retry={retry} renderReady={(data) => <p className="text-xl font-bold text-primary">{formatCurrency(centsToEuros(data.cents))}</p>} />
    </Card>
  );
};

// ─── Kachel — Chart #1 „Umsatz vs. Ausgaben" (M-F, §16) ─────────────────────

type RevenueExpenseChartData = { income: IncomeEntry[]; expenses: Expense[] };

/**
 * Chart-Kachel „Umsatz vs. Ausgaben, letzte 6 Monate" (§16 Chart #1).
 * Erhält die Rohdaten von {@link RevenueTile}/{@link ExpensesTile} bereits
 * geladen als Props (§6 „0 zusätzliche Firestore-Reads" — keine eigene
 * Erst-Ladung). Ein expliziter Retry-Klick führt einen ISOLIERTEN,
 * eigenständigen Re-Fetch nur dieser Kachel aus (Kachel-Isolation bleibt
 * auch im Fehlerfall gewahrt).
 */
const RevenueExpenseChartTile: React.FC<{
  scopeUid: string;
  income: IncomeEntry[] | null;
  incomeError: boolean;
  expenses: Expense[] | null;
  expensesError: boolean;
  expensesEnabled: boolean;
}> = ({ scopeUid, income, incomeError, expenses, expensesError, expensesEnabled }) => {
  const { t, i18n } = useTranslation('cockpit');
  const navigate = useNavigate();
  const [manualState, setManualState] = useState<ChartLoadState<RevenueExpenseChartData> | null>(null);

  const bubbledReady = income !== null && (!expensesEnabled || expenses !== null);
  const bubbledError = incomeError || (expensesEnabled && expensesError);
  const derivedState: ChartLoadState<RevenueExpenseChartData> =
    manualState ??
    (bubbledError
      ? { status: 'error' }
      : bubbledReady
        ? { status: 'ready', data: { income: income as IncomeEntry[], expenses: expenses ?? [] } }
        : { status: 'loading' });

  const retry = async () => {
    setManualState({ status: 'loading' });
    try {
      const [inc, exp] = await Promise.all([
        loadIncomeForUser(scopeUid),
        expensesEnabled ? getExpensesForUser(scopeUid) : Promise.resolve([] as Expense[]),
      ]);
      setManualState({ status: 'ready', data: { income: inc, expenses: exp } });
    } catch (err) {
      setManualState({ status: 'error' });
      logger.error('CockpitPage: Chart-Retry (Umsatz vs. Ausgaben) fehlgeschlagen', err);
    }
  };

  // E5 (§8.5): Umsatz-Wachstums-Badge (YoY des letzten VOLLEN Monats) im
  // Chart-Header. `null`-YoY ⇒ kein Badge (kein „— %"; GrowthBadge rendert dann
  // ohnehin `null`). Basis sind die bereits gebubbelten `income`-Rohdaten (0 Reads).
  const growth = income !== null ? computeRevenueGrowth(income, todayISO()) : null;
  const growthBadge =
    growth && growth.yoyPct !== null ? (
      <GrowthBadge
        pct={growth.yoyPct}
        label={t('cfo.growth.yoy', {
          month: formatMonthNameShort(growth.refYearMonth, i18n.language),
          pct: formatSignedPercent(growth.yoyPct),
        })}
        ariaLabel={t('cfo.growth.yoyAria', {
          month: formatMonthNameShort(growth.refYearMonth, i18n.language),
          pct: formatSignedPercent(growth.yoyPct),
        })}
      />
    ) : undefined;

  return (
    <ChartCard<RevenueExpenseChartData>
      title={t('charts.revenueExpense.title')}
      titleAccessory={growthBadge}
      state={derivedState}
      onRetry={retry}
      onClick={() => navigate('/dashboard/archiv')}
      toggleToTableLabel={t('charts.toggleToTable')}
      toggleToChartLabel={t('charts.toggleToChart')}
      emptyDashLabel={t('empty.dash')}
      retryLabel={t('retry')}
      ariaLabel={(data) => {
        const buckets = bucketMonthly(data.income, data.expenses, todayISO(), 6);
        const entries = buckets.map((b) => {
          const incomeStr = `${t('charts.revenueExpense.seriesIncome')} ${formatCurrency(centsToEuros(b.incomeCents))}`;
          const expenseStr = expensesEnabled
            ? `, ${t('charts.revenueExpense.seriesExpense')} ${formatCurrency(centsToEuros(b.expenseCents))}`
            : '';
          return `${formatMonthShort(b.yearMonth, i18n.language)}: ${incomeStr}${expenseStr}.`;
        });
        return `${t('charts.revenueExpense.ariaLabelIntro')} ${entries.join(' ')}`;
      }}
      renderChart={(data) => (
        <RevenueExpenseChart
          buckets={bucketMonthly(data.income, data.expenses, todayISO(), 6)}
          showExpenses={expensesEnabled}
          incomeLabel={t('charts.revenueExpense.seriesIncome')}
          expenseLabel={t('charts.revenueExpense.seriesExpense')}
          locale={i18n.language}
          reducedMotion={prefersReducedMotion()}
          formatCurrency={(cents) => formatCurrency(centsToEuros(cents))}
        />
      )}
      renderTable={(data) => {
        const buckets: MonthlyBucket[] = bucketMonthly(data.income, data.expenses, todayISO(), 6);
        return (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-secondary">
                <th className="py-1 pr-3 font-medium">{t('charts.revenueExpense.tableColumnMonth')}</th>
                <th className="py-1 pr-3 font-medium">{t('charts.revenueExpense.tableColumnIncome')}</th>
                {expensesEnabled && (
                  <th className="py-1 font-medium">{t('charts.revenueExpense.tableColumnExpense')}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => (
                <tr key={b.yearMonth} className="text-primary">
                  <td className="py-1 pr-3">{formatMonthShort(b.yearMonth, i18n.language)}</td>
                  <td className="py-1 pr-3">{formatCurrency(centsToEuros(b.incomeCents))}</td>
                  {expensesEnabled && <td className="py-1">{formatCurrency(centsToEuros(b.expenseCents))}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        );
      }}
    />
  );
};

// ─── Kachel — Chart #2 „Offene-Posten-Alterung" (M-F, §16) ──────────────────

/**
 * Chart-Kachel „Offene-Posten-Alterung" (§16 Chart #2). Erhält die
 * Rohdaten von {@link OpenItemsTile} bereits geladen als Prop (§6 „0
 * zusätzliche Firestore-Reads"). Klick auf die Kachel navigiert zu den
 * offenen Posten (Mahn-Kandidaten, §16 „direkt handlungsauslösend").
 */
const OpenItemsAgingChartTile: React.FC<{
  scopeUid: string;
  rows: CockpitOpenItemRow[] | null;
  rowsError: boolean;
}> = ({ scopeUid, rows, rowsError }) => {
  const { t, i18n } = useTranslation('cockpit');
  const navigate = useNavigate();
  const [manualState, setManualState] = useState<ChartLoadState<CockpitOpenItemRow[]> | null>(null);

  const derivedState: ChartLoadState<CockpitOpenItemRow[]> =
    manualState ?? (rowsError ? { status: 'error' } : rows !== null ? { status: 'ready', data: rows } : { status: 'loading' });

  const retry = async () => {
    setManualState({ status: 'loading' });
    try {
      const freshRows = await loadOpenItemRows(scopeUid);
      setManualState({ status: 'ready', data: freshRows });
    } catch (err) {
      setManualState({ status: 'error' });
      logger.error('CockpitPage: Chart-Retry (OP-Alterung) fehlgeschlagen', err);
    }
  };

  const bucketLabels: Record<AgingBucketId, string> = {
    notDue: t('charts.aging.buckets.notDue'),
    due1to30: t('charts.aging.buckets.due1to30'),
    due31to60: t('charts.aging.buckets.due31to60'),
    due61plus: t('charts.aging.buckets.due61plus'),
    noDueDate: t('charts.aging.buckets.noDueDate'),
  };

  return (
    <ChartCard<CockpitOpenItemRow[]>
      title={t('charts.aging.title')}
      subtitle={<p className="text-xs text-muted">{t('charts.aging.distinction')}</p>}
      state={derivedState}
      onRetry={retry}
      onClick={() => navigate('/dashboard/offene-posten')}
      toggleToTableLabel={t('charts.toggleToTable')}
      toggleToChartLabel={t('charts.toggleToChart')}
      emptyDashLabel={t('empty.dash')}
      retryLabel={t('retry')}
      ariaLabel={(data) => {
        const buckets = ageOpenItems(data, todayISO());
        const entries = buckets.map(
          (b) =>
            `${bucketLabels[b.id]}: ${formatCurrency(centsToEuros(b.amountCents))}, ${t('charts.aging.count', { count: b.count })}.`,
        );
        return `${t('charts.aging.ariaLabelIntro')} ${entries.join(' ')}`;
      }}
      renderChart={(data) => (
        <OpenItemsAgingChart
          buckets={ageOpenItems(data, todayISO())}
          bucketLabels={bucketLabels}
          reducedMotion={prefersReducedMotion()}
          formatCurrency={(cents) => formatCurrency(centsToEuros(cents))}
          formatCount={(count) => t('charts.aging.count', { count })}
          locale={i18n.language}
        />
      )}
      renderTable={(data) => {
        const buckets: AgingBucket[] = ageOpenItems(data, todayISO());
        return (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-secondary">
                <th className="py-1 pr-3 font-medium">{t('charts.aging.tableColumnBucket')}</th>
                <th className="py-1 pr-3 font-medium">{t('charts.aging.tableColumnAmount')}</th>
                <th className="py-1 font-medium">{t('charts.aging.tableColumnCount')}</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => (
                <tr key={b.id} className="text-primary">
                  <td className="py-1 pr-3">{bucketLabels[b.id]}</td>
                  <td className="py-1 pr-3">{formatCurrency(centsToEuros(b.amountCents))}</td>
                  <td className="py-1">{b.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }}
    />
  );
};

// ─── Kachel #4 — Posteingang (neu) ───────────────────────────────────────────

const InboxTile: React.FC<{ scopeUid: string }> = ({ scopeUid }) => {
  const navigate = useNavigate();
  const gate = canUseInbound(scopeUid);
  const { state, retry } = useCockpitTile<CockpitBoundedCount>(gate, scopeUid, loadInboundNewCount);

  if (!gate) return null;

  const target = UNIFIED_INBOX_ENABLED ? '/dashboard/eingaenge?stream=email' : '/dashboard/posteingang';

  return (
    <CompactCountTile<CockpitBoundedCount>
      icon={<IconInbox />}
      titleKey="inbox.title"
      sublabelKey="inbox.sublabel"
      onNavigate={() => navigate(target)}
      state={state}
      retry={retry}
      getCount={(data) => data.count}
      formatCount={formatBoundedCount}
    />
  );
};

// ─── Kachel #5 — Bank-Abgleich (offen) ───────────────────────────────────────

const BankTile: React.FC<{ uid: string | undefined; tier: Tier }> = ({ uid, tier }) => {
  const navigate = useNavigate();
  const gate = canUseBankReconcile(uid, tier);
  const { state, retry } = useCockpitTile<CockpitBoundedCount>(gate, uid ?? '', loadBankUnmatchedCount);

  if (!gate) return null;

  return (
    <CompactCountTile<CockpitBoundedCount>
      icon={<IconBank />}
      titleKey="bank.title"
      sublabelKey="bank.sublabel"
      onNavigate={() => navigate('/dashboard/bank-abgleich')}
      state={state}
      retry={retry}
      getCount={(data) => data.count}
      formatCount={formatBoundedCount}
    />
  );
};

// ─── Kachel #6 — Belege (offen zu buchen) ────────────────────────────────────

/**
 * Kachel #6 lädt ALLE gescannten Belege des Scopes (nicht nur `loadScannedExtractedCount`s
 * gefilterten Zähler), damit die Aktivierungs-Checkliste (Kachel #10, `firstReceiptScanned`)
 * dieselbe Firestore-Anfrage wiederverwenden kann (§1 „Wiederverwendung Kachel-6-Datenquelle,
 * kein Extra-Read") — der `status==='extracted'`-Filter (identisch zu
 * `cockpitLoaders.loadScannedExtractedCount`) wird hier inline repliziert, um einen
 * doppelten Firestore-Read derselben Collection zu vermeiden.
 */
const ReceiptsTile: React.FC<{
  scopeUid: string;
  uid: string | undefined;
  tier: Tier;
  onLoaded?: (totalCount: number) => void;
  /** §B: feuert bei `ready` ODER `error` (Settled-Signal für die Checkliste).
   *  Bei geschlossenem Gate rendert die Kachel `null` und feuert nie — den
   *  Gate-Fall behandelt der Aufrufer (`CockpitPage`) selbst als „settled". */
  onSettled?: () => void;
}> = ({ scopeUid, uid, tier, onLoaded, onSettled }) => {
  const navigate = useNavigate();
  const gate = canUseReceiptScan(uid, tier);
  const { state, retry } = useCockpitTile<{ extractedCount: number; totalCount: number }>(
    gate,
    scopeUid,
    async (scope) => {
      const receipts = await getScannedReceiptsForUser(scope);
      return {
        extractedCount: receipts.filter((r) => r.status === 'extracted').length,
        totalCount: receipts.length,
      };
    },
  );

  useEffect(() => {
    if (state.status === 'ready') {
      if (onLoaded) onLoaded(state.data.totalCount);
      if (onSettled) onSettled();
    } else if (state.status === 'error') {
      if (onSettled) onSettled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  if (!gate) return null;

  return (
    <CompactCountTile<{ extractedCount: number; totalCount: number }>
      icon={<IconScan />}
      titleKey="receipts.title"
      sublabelKey="receipts.sublabel"
      onNavigate={() => navigate('/dashboard/belege')}
      state={state}
      retry={retry}
      getCount={(data) => data.extractedCount}
      formatCount={(data) => `${data.extractedCount}`}
    />
  );
};

// ─── Kachel #7 — Steuer-Countdown (USt-Voranmeldung), Jurist-Doppel-Gate ────

const TaxCountdownTile: React.FC = () => {
  const { t, i18n } = useTranslation('cockpit');
  const navigate = useNavigate();

  if (!USTVA_ENABLED) return null;

  const { deadlineISO, daysRemaining } = computeNextUstvaDeadline(todayISO());

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => navigate('/dashboard/umsatzsteuer')}
      onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard/umsatzsteuer')}
      className="cursor-pointer"
    >
      <div className="flex items-center gap-2 mb-2 text-secondary">
        <IconVat aria-hidden={true} />
        <h3 className="text-sm font-semibold">{t('tax.title')}</h3>
      </div>
      {deadlineISO === null || daysRemaining === null ? (
        <p className="text-secondary">{t('empty.dash')}</p>
      ) : (
        <>
          <p className="text-sm font-medium text-primary">
            {daysRemaining > 0 &&
              t('tax.ustvaCountdown', {
                count: daysRemaining,
                deadline: new Date(`${deadlineISO}T00:00:00Z`).toLocaleDateString(i18n.language, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  timeZone: 'UTC',
                }),
              })}
            {daysRemaining === 0 &&
              t('tax.ustvaCountdownDueToday', {
                deadline: new Date(`${deadlineISO}T00:00:00Z`).toLocaleDateString(i18n.language, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  timeZone: 'UTC',
                }),
              })}
            {daysRemaining < 0 &&
              t('tax.ustvaCountdownOverdue', {
                deadline: new Date(`${deadlineISO}T00:00:00Z`).toLocaleDateString(i18n.language, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  timeZone: 'UTC',
                }),
              })}
          </p>
          <p className="text-xs text-secondary mt-2">{t('tax.disclaimer')}</p>
        </>
      )}
    </Card>
  );
};

// ─── Kachel #9 — Quick-Actions ────────────────────────────────────────────────

const QuickActionsTile: React.FC<{ ctx: NavContext }> = ({ ctx }) => {
  const { t: tCockpit } = useTranslation('cockpit');
  const { t: tNav } = useTranslation('navigation');
  const navigate = useNavigate();

  const visibleActions = NAV_ACTIONS.filter((action) => action.visibility(ctx) === 'visible');
  if (visibleActions.length === 0) return null;

  return (
    <Card>
      <h3 className="text-sm font-semibold text-secondary mb-3">{tCockpit('quickActions.title')}</h3>
      <div className="flex flex-wrap gap-2">
        {visibleActions.map((action) => (
          <Button key={action.id} variant="secondary" icon={<action.icon />} onClick={() => navigate(action.route)}>
            {tNav(action.labelKey)}
          </Button>
        ))}
      </div>
    </Card>
  );
};

// ─── Kachel #10 — Aktivierungs-Checkliste ────────────────────────────────────

const ChecklistTile: React.FC<{
  items: ChecklistItem[];
  visibleIds: ReadonlySet<string>;
  /** §B: `true` erst, wenn die zählerbasierten Inputs (Umsatz-/Belege-Kachel)
   *  settled sind. Bis dahin rendert die Kachel `null` (fail-quiet: lieber
   *  keine Checkliste als eine mit falsch-offenen Zeilen, die dann zublendet). */
  inputsSettled: boolean;
}> = ({ items, visibleIds, inputsSettled }) => {
  const { t } = useTranslation('cockpit');
  const navigate = useNavigate();

  const visibleItems = items.filter((item) => visibleIds.has(item.id));

  // §B Reihenfolge der Null-Äste ist normativ:
  //  1. kein Karten-Mount vor Settled (kein Flacker-Frame, kein Auf-dann-Zublenden),
  //  2. nichts anzuzeigen ⇒ weg,
  //  3. ALLE sichtbaren Schritte erledigt ⇒ Karte weg (behebt „alles abgehakt").
  if (!inputsSettled) return null;
  if (visibleItems.length === 0) return null;
  if (visibleItems.every((item) => item.done)) return null;

  return (
    <Card>
      <h3 className="text-sm font-semibold text-secondary mb-3">{t('checklist.title')}</h3>
      <ul className="space-y-2">
        {visibleItems.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => navigate(item.deepLinkRoute)}
              className="flex items-center gap-2 text-sm text-left w-full hover:text-primary transition-colors"
            >
              {item.done ? (
                <IconSuccess className="text-accent-ink shrink-0" aria-hidden={true} />
              ) : (
                <IconCircle className="text-faint shrink-0" aria-hidden={true} />
              )}
              <span className={item.done ? 'line-through text-secondary' : 'text-primary'}>
                {t(`checklist.${item.id}`)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
};

// ─── CockpitPage (Orchestrierung) ────────────────────────────────────────────

/**
 * Owner-Index-Landing (§1). Wird von `routes/dashboardChildren.tsx` nur
 * gerendert, wenn `UX_COCKPIT_ENABLED` true ist — bereits in
 * `<AdvisorHidden>` gewrapped (E4, `AdvisorHidden` unverändert), rendert also
 * nie für einen Mandanten-Modus-Advisor.
 */
/** Konservative Anzeige-Rundung auf volle Euro AUFWÄRTS (Muster `VatReserveTile`). */
function ceilToEuroCents(cents: number): number {
  return Math.ceil(cents / 100) * 100;
}

/**
 * Hero-Kachel 2 „Überfällige Forderungen" (v5 §6.2): rein aus den bereits
 * gebubbelten offenen-Posten-Zeilen via {@link summarizeOpenItems} (0 zusätzliche
 * Firestore-Reads). Solange die Zeilen nicht vorliegen (Laden ODER Ladefehler)
 * zeigt die Kachel einen neutralen Platzhalter statt einer erfundenen Null.
 */
const OverdueHeroTile: React.FC<{
  rows: CockpitOpenItemRow[] | null;
  rowsError: boolean;
  onNavigate: () => void;
}> = ({ rows, rowsError, onNavigate }) => {
  const { t } = useTranslation('cockpit');
  const label = t('cfo.hero.overdue');

  if (rows === null || rowsError) {
    return (
      <CockpitHeroTile
        label={label}
        degradedSlot={<span className="text-sm text-muted">{t('cfo.hero.calculating')}</span>}
        onClick={onNavigate}
      />
    );
  }

  const summary = summarizeOpenItems(rows);
  return (
    <CockpitHeroTile
      label={label}
      cents={summary.overdueCents}
      sub={t('cfo.hero.overdueSub', { count: summary.overdueCount })}
      tone={summary.overdueCents > 0 ? 'danger' : 'default'}
      onClick={onNavigate}
    />
  );
};

/**
 * Hero-Kachel 3 „Nächste Steuerzahlung" (v5 §6.2): UStVA-Zahllast + Termin aus
 * dem bestehenden vatReserve-Pfad ({@link buildVatReturn} × {@link deriveVatReserveView}
 * × {@link computeNextUstvaDeadline}). Die USt-Einnahmen werden NICHT mehr hier
 * geladen, sondern kommen als Prop von der Seite (`vatIncome`) — EIN gemeinsamer
 * Read speist Hero-3 UND die Netto-Cash-Vorschau (§6.3 USt-Abfluss), sodass die
 * Netto-Cash-Kachel 0 zusätzliche Reads verursacht. Der Read fällt — exakt wie in
 * {@link VatReserveTile} — NUR im Soll-Nicht-KU-Pfad an; alle anderen Fälle (Gate
 * aus, §19-KU, Ist-Versteuerung, fehlende Versteuerungsart) laden NICHTS.
 * Fail-closed: ausschliesslich der echte `reserve`-Zustand (positive Zahllast)
 * zeigt eine Zahl; jeder andere Zustand spiegelt den degradierten VatReserve-
 * Zustand als neutralen Platzhalter OHNE Steueraussage (KEINE erfundene Null) —
 * der ausführliche jurist-freigegebene Wortlaut lebt in der `VatReserveTile` der
 * Detail-Zone.
 *
 * BUGFIX (md5-c1fix): `cfo.hero.calculating` ("wird berechnet …") darf NUR im
 * echten Lade-Zustand erscheinen (`needsIncomeLoad && !vatIncomeSettled`).
 * Vorher war das der Catch-all für JEDEN Nicht-reserve-Zustand — u. a. für den
 * mit Abstand häufigsten Fall `taxationMode` unset (needsIncomeLoad=false),
 * §19-Kleinunternehmer und Ist-Versteuerung — die Kachel blieb dort DAUERHAFT
 * auf „wird berechnet …" hängen. {@link deriveVatReserveView} wird jetzt auch
 * im Sync-Pfad (`!needsIncomeLoad`) ausgewertet (Muster `VatReserveTile`s
 * Sync-Zweig) und jeder `view.kind` bekommt einen eigenen, informativen
 * Degradiert-Text statt des irreführenden „wird berechnet …".
 */
const NextTaxHeroTile: React.FC<{
  expenses: Expense[] | null;
  isSmallBusiness: boolean | undefined;
  taxationMode: 'soll' | 'ist' | undefined;
  /** Gebubbelte USt-Einnahmen (von der Seite geladen); `null` = noch nicht / nicht anwendbar. */
  vatIncome: VatIncomeEntry[] | null;
  /** Ladefehler der USt-Einnahmen (fail-closed ⇒ degradierter Platzhalter). */
  vatIncomeError: boolean;
  /** `true`, sobald der USt-Einnahmen-Read (oder sein Nicht-Anfall) abgeschlossen ist. */
  vatIncomeSettled: boolean;
  onNavigate: () => void;
}> = ({ expenses, isSmallBusiness, taxationMode, vatIncome, vatIncomeError, vatIncomeSettled, onNavigate }) => {
  const { t, i18n } = useTranslation('cockpit');
  const navigate = useNavigate();
  const today = todayISO();
  const deadline = computeNextUstvaDeadline(today);
  const needsIncomeLoad = USTVA_ENABLED && isSmallBusiness !== true && taxationMode === 'soll';

  const label = t('cfo.hero.nextTax');
  const renderDegraded = (content: React.ReactNode, onClick: () => void = onNavigate) => (
    <CockpitHeroTile
      label={label}
      degradedSlot={<span className="text-sm text-muted">{content}</span>}
      onClick={onClick}
    />
  );

  // Echter Lade-Zustand (Soll-Nicht-KU-Pfad, USt-Einnahmen-Read läuft noch) —
  // NUR hier darf „wird berechnet …" stehen.
  if (needsIncomeLoad && !vatIncomeSettled) return renderDegraded(t('cfo.hero.calculating'));

  // Echter Ladefehler des EINEN benötigten Reads: KEINE Figures aus leerem
  // `vatIncome` fingieren (das wäre eine erfundene „keine Zahllast"-Aussage,
  // Auflage „KEINE erfundene Null") — eigener, nicht-„calculating" Platzhalter.
  // Der Retry-CTA lebt bereits in der Detail-Kachel `VatReserveTile`.
  if (needsIncomeLoad && vatIncomeError) return renderDegraded(t('empty.dash'));

  const period = currentQuarterPeriod(today);
  const figures = buildVatReturn(
    vatIncome ?? [],
    toVatInputAggregate(expenses ?? [], period),
    period,
    'soll',
    isSmallBusiness === true,
  );
  const view = deriveVatReserveView({
    ustvaEnabled: USTVA_ENABLED,
    isSmallBusiness,
    taxationMode,
    figures,
    deadline,
  });

  switch (view.kind) {
    case 'reserve': {
      let sub: string | undefined;
      if (view.deadlineISO) {
        const deadlineLabel = new Date(`${view.deadlineISO}T00:00:00Z`).toLocaleDateString(i18n?.language, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'UTC',
        });
        sub = t('cfo.hero.taxDue', { deadline: deadlineLabel });
      }
      return (
        <CockpitHeroTile label={label} cents={ceilToEuroCents(view.amountCents)} sub={sub} onClick={onNavigate} />
      );
    }
    case 'refund':
      // Realer Berechnungswert (kein erfundener Platzhalter) — kompakt als Text
      // statt als Betrags-Slot, da das Hero-Label „Nächste USt-Zahlung" für eine
      // Erstattung fachlich nicht passt (kein „Zahlung fällig").
      return renderDegraded(
        t('cfo.vatReserve.refund', { betrag: formatCurrency(centsToEuros(ceilToEuroCents(view.amountCents))) }),
      );
    case 'zero':
      return renderDegraded(t('cfo.vatReserve.zero'));
    case 'istPending':
      return renderDegraded(t('cfo.vatReserve.istPending'));
    case 'configure':
      // Navigationsziel bewusst wie `VatReserveTile` auf `/dashboard/konto`
      // (Firmenprofil) statt `onNavigate` (→ /dashboard/umsatzsteuer): die
      // Versteuerungsart wird im Firmenprofil gesetzt, nicht in der
      // USt-Übersicht — der CTA muss dorthin führen, wo die Aktion möglich ist.
      return renderDegraded(t('cfo.vatReserve.configureCta'), () => navigate('/dashboard/konto'));
    case 'smallBusinessReverseCharge':
      // Legal-sensibler §13b-Hinweis (potenzielle USt-Pflicht trotz §19) — wird
      // NICHT zu „—" verkürzt, das würde die Meldepflicht-Information verlieren.
      return renderDegraded(t('cfo.vatReserve.smallBusinessReverseCharge'));
    case 'hidden':
    default:
      // G2 (Gate aus) oder normaler §19-KU ohne §13b-Pflicht: keine Steueraussage
      // möglich/nötig — die Hero-Zeile hat feste 3 Slots, daher kein `null`.
      return renderDegraded(t('empty.dash'));
  }
};

const CockpitPage: React.FC = () => {
  const { t } = useTranslation('cockpit');
  const navigate = useNavigate();
  const { currentUser, sellerData, isPremium, effectiveScopeUid } = useAuth();
  // Pricing-Repackaging §3.2: effektiver Tier für die Ultra-Gates (Bank/Scan).
  const tier = resolveTier(sellerData);
  const isAdvisorViewingClient = useIsReadOnly();
  const isAdvisorContext = useIsAdvisorContext();

  // §1 Kachel-10 Inputs — jeweils eigener isolierter Lade-Zustand (Kachel-
  // Isolation gilt auch für Checklisten-Inputs, s. Modul-Kopf ReceiptsTile).
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [scannedReceiptCount, setScannedReceiptCount] = useState(0);
  const [inboundMailboxExists, setInboundMailboxExists] = useState(false);
  const [advisorInvited, setAdvisorInvited] = useState(false);

  // §B Anti-Flacker: die Aktivierungs-Checkliste darf ihren Sichtbarkeits-
  // Entscheid (inkl. „alle erledigt ⇒ weg") erst treffen, wenn die
  // zählerbasierten Inputs settled sind. `invoiceCount`/`scannedReceiptCount`
  // starten bei 0 und werden erst nach Kachel-Load gesetzt — ein naives „alles
  // done ⇒ hide" würde die Karte sonst kurz mit falsch-offenen Zeilen zeigen.
  const [revenueSettled, setRevenueSettled] = useState(false);
  const [receiptsSettledFromTile, setReceiptsSettledFromTile] = useState(false);

  // M-F (§6 „0 zusätzliche Firestore-Reads"): die Chart-Kacheln bekommen die
  // bereits von den Zahlen-Kacheln geladenen Rohdaten hier hoch gereicht,
  // statt sie selbst nochmal zu laden. `null` = „diese Kachel hat noch nicht
  // geladen" (Chart zeigt dann ebenfalls einen Ladezustand).
  const [incomeEntries, setIncomeEntries] = useState<IncomeEntry[] | null>(null);
  const [incomeError, setIncomeError] = useState(false);
  const [expenseEntries, setExpenseEntries] = useState<Expense[] | null>(null);
  const [expensesError, setExpensesError] = useState(false);
  const [openItemRows, setOpenItemRows] = useState<CockpitOpenItemRow[] | null>(null);
  const [openItemRowsError, setOpenItemRowsError] = useState(false);
  // v5 §6.2/§6.3: EIN gemeinsamer USt-Einnahmen-Read speist Hero-3 „Nächste
  // Steuerzahlung" UND den USt-Abfluss der Netto-Cash-Vorschau (§6.3) — die
  // Netto-Cash-Kachel verursacht dadurch 0 zusätzliche Firestore-Reads. Der Read
  // fällt (wie in `VatReserveTile`) NUR im Soll-Nicht-KU-Pfad an (§8.2).
  const nettoCashVatEligible =
    USTVA_ENABLED && sellerData?.isSmallBusiness !== true && sellerData?.taxationMode === 'soll';
  const [vatIncomeEntries, setVatIncomeEntries] = useState<VatIncomeEntry[] | null>(null);
  const [vatIncomeError, setVatIncomeError] = useState(false);
  const [vatIncomeSettled, setVatIncomeSettled] = useState(!nettoCashVatEligible);

  const showAdvisorInviteRow = canInviteAdvisor(currentUser?.uid, sellerData);
  // Gate-Spiegel des ReceiptsTile: bei geschlossenem Gate rendert die Kachel
  // `null` und feuert nie `onSettled` ⇒ der Gate-Fall zählt hier direkt als
  // settled (kein hängendes „warte auf eine Kachel, die es nie gibt").
  const receiptsGateOpen = canUseReceiptScan(currentUser?.uid, tier);
  const receiptsSettled = !receiptsGateOpen || receiptsSettledFromTile;
  const inputsSettled = revenueSettled && receiptsSettled;

  // Mailbox-Existenz (Muster `InboxPage.tsx:167-191` — currentUser.uid, nicht
  // effectiveScopeUid; eigener try/catch, non-critical bei Fehler).
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(fsDoc(db, 'inbound_mailboxes', currentUser.uid));
        if (!cancelled) setInboundMailboxExists(snap.exists());
      } catch (err) {
        if (!cancelled) setInboundMailboxExists(false);
        logger.error('CockpitPage: Mailbox-Check fehlgeschlagen (non-critical)', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // Gesendete Steuerberater-Einladungen — nur laden, wenn die Checklisten-
  // Zeile überhaupt sichtbar wäre (kein unnötiger Callable-Call).
  useEffect(() => {
    if (!showAdvisorInviteRow) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await listMyAdvisorInvites();
        if (!cancelled) setAdvisorInvited(result.invites.length > 0);
      } catch (err) {
        if (!cancelled) setAdvisorInvited(false);
        logger.error('CockpitPage: Einladungs-Check fehlgeschlagen (non-critical)', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showAdvisorInviteRow]);

  // USt-Einnahmen laden — gleiche Gate-Bedingung wie bisher der Hero-3-Read
  // (Soll-Nicht-KU-Pfad). §19-KU/Ist/kein-Modus laden NICHTS (Read-Ökonomie §8.2).
  useEffect(() => {
    if (!nettoCashVatEligible) {
      setVatIncomeEntries(null);
      setVatIncomeError(false);
      setVatIncomeSettled(true);
      return;
    }
    let cancelled = false;
    setVatIncomeSettled(false);
    setVatIncomeError(false);
    loadVatIncomeForUser(effectiveScopeUid)
      .then((data) => {
        if (!cancelled) {
          setVatIncomeEntries(data);
          setVatIncomeSettled(true);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setVatIncomeError(true);
          setVatIncomeSettled(true);
        }
        logger.error('CockpitPage: USt-Einnahmen-Read (Hero-3 + Netto-Cash) fehlgeschlagen', err);
      });
    return () => {
      cancelled = true;
    };
  }, [nettoCashVatEligible, effectiveScopeUid]);

  const checklistItems = deriveChecklist({
    onboardingCompleted: sellerData?.onboardingCompleted === true,
    // §B: echte Pflichtfeld-Prüfung statt Wizard-Proxy `onboardingCompleted`.
    profileFieldsComplete: hasSellerRequiredFields(sellerData),
    inboundMailboxExists,
    invoiceCount,
    advisorInvited,
    scannedReceiptCount,
  }).map((item) =>
    // Deep-Link-Override (§1 PFLICHT): der Loader setzt für `firstInvoiceCreated`
    // die heutige Index-Route `/dashboard` — bei Flag ON ist das jetzt das
    // Cockpit selbst (Self-Loop). CockpitPage biegt auf `INVOICE_FORM_ROUTE` um.
    item.id === 'firstInvoiceCreated' ? { ...item, deepLinkRoute: INVOICE_FORM_ROUTE } : item,
  );

  const checklistVisibleIds = new Set<string>([
    'profileComplete',
    'inboundAddressActive',
    'firstInvoiceCreated',
    'firstReceiptScanned',
    ...(showAdvisorInviteRow ? ['advisorInvited'] : []),
  ]);

  const navCtx: NavContext = {
    uid: currentUser?.uid,
    effectiveScopeUid,
    isPremium,
    tier,
    isAdvisorViewingClient,
    isAdvisorContext,
    sellerData,
  };

  // D1 (§3.2 Zone C): die kompakte Aktivitäts-Leiste rendert nur, wenn
  // mindestens eines ihrer drei Gates offen ist — sonst stünde ein leeres
  // Grid-Div mit doppeltem `gap` da. Spiegelt exakt die Kachel-Gates (jede
  // Kachel gated sich zusätzlich selbst via `if (!gate) return null`).
  const showActivityRow =
    canUseInbound(effectiveScopeUid) ||
    canUseBankReconcile(currentUser?.uid, tier) ||
    receiptsGateOpen;

  // M-CFO-5 (§8.2): entscheidet, ob `VatReserveTile` sichtbar rendert. Spiegelt
  // EXAKT die beiden `null`-Äste der Kachel (G2 `!USTVA_ENABLED`; G3 §19-KU ohne
  // §13b-Pflicht) — der Betrags-/configure-/istPending-Pfad rendert immer eine
  // Karte. Der §13b-Fall braucht die Reverse-Charge-Zahllast aus den gebubbelten
  // Ausgaben; ist die noch nicht geladen / fehlerhaft, gilt fail-closed „hidden"
  // (identisch zum Sync-Pfad der Kachel). Steuert die Zone-D-Suppression: der
  // USt-Countdown steckt bei sichtbarer Rücklage-Kachel bereits in dieser.
  const vatReserveWillRender = ((): boolean => {
    if (!USTVA_ENABLED) return false;
    if (sellerData?.isSmallBusiness === true) {
      if (expenseEntries === null || expensesError) return false;
      const period = currentQuarterPeriod(todayISO());
      const figures = buildVatReturn([], toVatInputAggregate(expenseEntries, period), period, 'soll', true);
      return figures.smallBusinessHasReverseChargeDuty === true;
    }
    return true;
  })();

  // ── Netto-Cash-Vorschau (§6.3) — aus bereits gebubbelten Rohdaten komponiert ──
  // (offene Posten + Ausgaben + USt-Zahllast; DSO pure aus den Zeilen). 0 neue
  // Reads. `vatReturn===null` (KU/Ist/kein-Modus/Ladefehler) ⇒ „ohne USt-Abfluss".
  const nettoCashToday = todayISO();
  const nettoCashUstvaDeadlineISO = computeNextUstvaDeadline(nettoCashToday).deadlineISO;
  // USt-Abfluss ist erst berechenbar, wenn USt-Einnahmen UND Ausgaben bereit sind.
  const nettoCashVatPending =
    nettoCashVatEligible && !vatIncomeError && (!vatIncomeSettled || (expenseEntries === null && !expensesError));
  const nettoCashVatReturn: { payableCents: number } | null = (() => {
    if (!nettoCashVatEligible || vatIncomeError) return null; // fail-closed ⇒ Hinweis
    if (!vatIncomeSettled || expenseEntries === null) return null; // noch nicht berechenbar (pending)
    const period = currentQuarterPeriod(nettoCashToday);
    const figures = buildVatReturn(
      vatIncomeEntries ?? [],
      toVatInputAggregate(expenseEntries, period),
      period,
      'soll',
      false,
    );
    return { payableCents: figures.kz83PayableCents };
  })();

  // Hero-1 „Netto-Cash 30 Tage" = Summe der ersten 4 Wochen; nur wenn alle drei
  // Quellen bereit sind, sonst degradierter Platzhalter (KEINE erfundene Null).
  const nettoCashReady =
    openItemRows !== null &&
    !openItemRowsError &&
    (!EXPENSES_ENABLED || expenseEntries !== null) &&
    !expensesError &&
    !nettoCashVatPending;
  const heroNetCash30dCents = nettoCashReady
    ? sumNetForWeeks(
        computeNettoCash({
          openItems: openItemRows ?? [],
          dso: computeDso(openItemRows ?? [], nettoCashToday),
          expenses: expenseEntries ?? [],
          vatReturn: nettoCashVatReturn,
          ustvaDeadlineISO: nettoCashUstvaDeadlineISO,
          todayISO: nettoCashToday,
        }),
        4,
      )
    : null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-primary mb-6">{t('title')}</h1>
      {/* M-UX-7.3 (§3, E4): unconditional mount - the card itself owns the
          visibility gate (outer/inner-less self-gate, pattern:
          MainLayout.tsx:686-691 banners). Owner and advisor-with-complete-
          profile both see `null` here -> zero DOM delta for them. */}
      <KanzleiProfileCard />
      {/* M-CFO-5 (§10): Zonen-Layout als `flex flex-col` mit `order`-Utilities.
          DESKTOP-Reihenfolge = xl:order (A1 → A2 → Charts → Überfällig-Fokus →
          Zähler → Rest). MOBILE-Reihenfolge = order (die Handlungs-Kacheln
          YTD/USt-Rücklage/DSO/Überfällig-Fokus stehen VOR den Charts, §10).
          Nur `order` variiert je Breakpoint — die Kachel-Verdrahtung
          (onRowsReady/onIncomeReady/onSettled … + Anti-Flacker `inputsSettled`,
          Chart-Bubbling) bleibt byte-identisch (§10 Verdrahtungs-Invariante). */}
      <div className="flex flex-col gap-4">
        {/* Zone HERO (NEU, v5 §6.2): 3 zahlengetriebene Survival-Kacheln mit dem
            Signature-Element (Buchungszahl + Kontostrich).
            (1) Netto-Cash 30 Tage — Summe der ersten 4 Wochen des Netto-Cash-
                Modells (§6.3, M-D5-C2); degradiert bis alle Quellen bereit sind.
            (2) Überfällige Forderungen — `summarizeOpenItems` (gebubbelt, 0 Reads).
            (3) Nächste Steuerzahlung — vatReserve-Pfad, fail-closed. */}
        <div className="order-1 xl:order-1 grid grid-cols-1 md:grid-cols-3 gap-4">
          <CockpitHeroTile
            label={t('cfo.hero.netCash30d')}
            cents={heroNetCash30dCents}
            degradedSlot={<span className="text-sm text-muted">{t('cfo.hero.calculating')}</span>}
            sub={heroNetCash30dCents !== null ? t('cfo.hero.netCashSub') : undefined}
            tone={heroNetCash30dCents !== null && heroNetCash30dCents < 0 ? 'danger' : 'default'}
            onClick={() => navigate('/dashboard/offene-posten')}
          />
          <OverdueHeroTile
            rows={openItemRows}
            rowsError={openItemRowsError}
            onNavigate={() => navigate('/dashboard/offene-posten')}
          />
          <NextTaxHeroTile
            expenses={expenseEntries}
            isSmallBusiness={sellerData?.isSmallBusiness}
            taxationMode={sellerData?.taxationMode}
            vatIncome={vatIncomeEntries}
            vatIncomeError={vatIncomeError}
            vatIncomeSettled={vatIncomeSettled}
            onNavigate={() => navigate('/dashboard/umsatzsteuer')}
          />
        </div>

        {/* Zone DETAIL (v5 §6.2, order-5): die vormalige Zone A1 rutscht als
            „vorletzte Reihe" hierher — Ergebnis YTD · USt-Rücklage · DSO (Detail
            zu den Hero-Kacheln 1/3). Die Zone-D-Suppression des Steuer-Countdowns
            bleibt an die VatReserve-Sichtbarkeit gekoppelt (unverändert). */}
        <div className="order-5 xl:order-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <ResultYtdTile
            scopeUid={effectiveScopeUid}
            tier={tier}
            income={incomeEntries}
            incomeError={incomeError}
            expenses={expenseEntries}
            expensesError={expensesError}
            expensesEnabled={EXPENSES_ENABLED}
            todayISO={todayISO()}
          />
          <VatReserveTile
            scopeUid={effectiveScopeUid}
            expenses={expenseEntries}
            expensesError={expensesError}
            isSmallBusiness={sellerData?.isSmallBusiness}
            taxationMode={sellerData?.taxationMode}
          />
          <DsoTile
            scopeUid={effectiveScopeUid}
            uid={currentUser?.uid}
            tier={tier}
            rows={openItemRows}
            rowsError={openItemRowsError}
          />
        </div>

        {/* Zone A2 (= bisherige Zone A, unverändert): Offene Posten · Umsatz · Ausgaben. */}
        <div className="order-3 xl:order-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <OpenItemsTile
            scopeUid={effectiveScopeUid}
            onRowsReady={(rows) => {
              setOpenItemRows(rows);
              // M-F-1 (Reviewer-Fix): Error-Latch zurücksetzen — sonst bleibt die
              // abhängige Chart-Kachel nach einem erfolgreichen Retry der
              // Zahlen-Kachel fälschlich im Fehlerzustand hängen.
              setOpenItemRowsError(false);
            }}
            onRowsError={() => setOpenItemRowsError(true)}
          />
          <RevenueTile
            scopeUid={effectiveScopeUid}
            onLoaded={setInvoiceCount}
            onSettled={() => setRevenueSettled(true)}
            onIncomeReady={(entries) => {
              setIncomeEntries(entries);
              // M-F-1 (Reviewer-Fix): s. OpenItemsTile oben.
              setIncomeError(false);
            }}
            onIncomeError={() => setIncomeError(true)}
          />
          <ExpensesTile
            scopeUid={effectiveScopeUid}
            onExpensesReady={(expenses) => {
              setExpenseEntries(expenses);
              // M-F-1 (Reviewer-Fix): s. OpenItemsTile oben.
              setExpensesError(false);
            }}
            onExpensesError={() => setExpensesError(true)}
          />
        </div>

        {/* Zone B — Charts (D2 revidiert: DREI Chart-Kacheln, xl 3-spaltig).
            Additive Kacheln, erben das UX_COCKPIT_ENABLED-Gate über die Route
            (keine neue Flag), Datengrundlage aus den bereits geladenen
            Zahlen-Kacheln der Zone A2 (§6 „0 zusätzliche Firestore-Reads").
            Innere `order`: Mobile Umsatz → Liquidität → Alterung; Desktop
            Umsatz → Alterung → Liquidität (§10 Mobile-Priorität). */}
        <div className="order-4 xl:order-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div className="order-1">
            <RevenueExpenseChartTile
              scopeUid={effectiveScopeUid}
              income={incomeEntries}
              incomeError={incomeError}
              expenses={expenseEntries}
              expensesError={expensesError}
              expensesEnabled={EXPENSES_ENABLED}
            />
          </div>
          <div className="order-3 xl:order-2">
            <OpenItemsAgingChartTile
              scopeUid={effectiveScopeUid}
              rows={openItemRows}
              rowsError={openItemRowsError}
            />
          </div>
          <div className="order-2 xl:order-3">
            <NettoCashTile
              scopeUid={effectiveScopeUid}
              rows={openItemRows}
              rowsError={openItemRowsError}
              expenses={expenseEntries}
              expensesError={expensesError}
              expensesEnabled={EXPENSES_ENABLED}
              vatReturn={nettoCashVatReturn}
              vatPending={nettoCashVatPending}
              ustvaDeadlineISO={nettoCashUstvaDeadlineISO}
            />
          </div>
        </div>

        {/* Zone B2 „Handlungs-Fokus" (v5 §6.4): links Überfällig-Fokus (halbbreit,
            Top-3-Handlungsliste), rechts DSO-Ampel-/Trend-Begleiter. Mobile direkt
            nach den Hero-Kacheln (order-2, VOR den Charts), Desktop unter den
            Charts (xl:order-4) — §10 Mobile-Priorität. */}
        <div className="order-2 xl:order-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <OverdueFocusList
            scopeUid={effectiveScopeUid}
            rows={openItemRows}
            rowsError={openItemRowsError}
          />
          <DsoTrendTile rows={openItemRows} rowsError={openItemRowsError} todayISO={todayISO()} />
        </div>

        {/* Zone C — kompakte Aktivitäts-Leiste (D1). Rendert nur, wenn
            mindestens ein Gate offen ist (kein leeres Grid-Div). */}
        {showActivityRow && (
          <div
            data-testid="cockpit-activity-row"
            className="order-6 xl:order-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3"
          >
            <InboxTile scopeUid={effectiveScopeUid} />
            <BankTile uid={currentUser?.uid} tier={tier} />
            <ReceiptsTile
              scopeUid={effectiveScopeUid}
              uid={currentUser?.uid}
              tier={tier}
              onLoaded={setScannedReceiptCount}
              onSettled={() => setReceiptsSettledFromTile(true)}
            />
          </div>
        )}

        {/* Zone D — Rest (order-7). Der Steuer-Countdown wird UNTERDRÜCKT, sobald
            die USt-Rücklage-Kachel (Zone DETAIL) sichtbar rendert — dort steckt
            der Countdown bereits (§8.2). Nur bei hidden-Rücklage (G2/G3) bleibt
            der eigenständige Countdown wie bisher. */}
        <div className="order-7 xl:order-7 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {!vatReserveWillRender && <TaxCountdownTile />}
          <QuickActionsTile ctx={navCtx} />
          <ChecklistTile
            items={checklistItems}
            visibleIds={checklistVisibleIds}
            inputsSettled={inputsSettled}
          />
        </div>
      </div>
    </div>
  );
};

export default CockpitPage;
